import { FocusMonitor, LiveAnnouncer } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { EllipsisTooltipDirective } from '../../directives';
import { HxIcon } from '../icon/icon';

export type HxTagVariant = 'filled' | 'outlined' | 'soft';
export type HxTagSize = 'sm' | 'md' | 'lg';
export type HxTagColor = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

/** Internal mapping from public color names to Moon context tokens. */
const COLOR_TO_CONTEXT_MAP = {
  default: 'neutral',
  primary: 'brand',
  success: 'positive',
  warning: 'caution',
  error: 'negative',
  info: 'info',
} as const;

/**
 * Tag component for displaying removable labels and categorizations.
 *
 * @example
 *   ```html
 *   <!-- Basic removable tag -->
 *   <hx-tag label="TypeScript" (removed)="onRemove()"></hx-tag>
 *
 *   <!-- Non-removable label -->
 *   <hx-tag label="Fixed Label" [removable]="false"></hx-tag>
 *
 *   <!-- Semantic color variant -->
 *   <hx-tag label="Success" color="success" variant="soft"></hx-tag>
 *
 *   <!-- Different sizes -->
 *   <hx-tag label="Small" size="sm"></hx-tag>
 *   <hx-tag label="Medium" size="md"></hx-tag>
 *   <hx-tag label="Large" size="lg"></hx-tag>
 *   ```;
 *
 * @see {@link https://ui.moondesignsystem.com/?path=/docs/components-chip--docs Moon Design System Chip}
 */
@Component({
  selector: 'hx-tag',
  standalone: true,
  templateUrl: './tag.html',
  styleUrls: ['./tag.css', './tag-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClasses()',
  },
  imports: [HxIcon, EllipsisTooltipDirective],
})
export class HxTagComponent extends HxBaseControl implements AfterViewInit, OnDestroy {
  @ViewChild('closeButton') closeButtonElement?: ElementRef<HTMLButtonElement>;
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  /**
   * Text content displayed in the tag.
   *
   * @required
   */
  readonly label = input.required<string>();

  /**
   * Whether to show the close button for tag removal.
   *
   * @default true
   */
  readonly removable = input<boolean>(true);

  /**
   * Visual style variant.
   *
   * - `filled`: Solid background, high contrast (default)
   * - `outlined`: Transparent background with border
   * - `soft`: Light tinted background, reduced contrast
   *
   * @default 'filled'
   */
  readonly variant = input<HxTagVariant>('filled');

  /**
   * Size variant affecting height, padding, and font-size.
   *
   * - `sm`: Small (24px height) - Compact spaces, dense layouts
   * - `md`: Medium (32px height) - Default, balanced size
   * - `lg`: Large (40px height) - Emphasis, touch-optimized
   *
   * @default 'md'
   */
  readonly size = input<HxTagSize>('md');

  /**
   * Semantic color for conveying meaning through color.
   *
   * - `default`: Neutral gray
   * - `primary`: Brand color (blue)
   * - `success`: Green (completed, successful)
   * - `warning`: Amber (attention needed)
   * - `error`: Red (errors, destructive)
   * - `info`: Blue/teal (informational)
   *
   * @default 'default'
   */
  readonly color = input<HxTagColor>('default');

  /**
   * Optional leading icon identifier.
   *
   * @default undefined
   */
  readonly startIcon = input<string | undefined>(undefined);

  /**
   * Maximum width for text truncation. Text exceeding this width will be truncated with ellipsis and shown in tooltip.
   *
   * @default '192px'
   */
  readonly maxWidth = input<string | undefined>('192px');

  /**
   * Additional CSS classes to apply to the tag.
   *
   * @default ''
   */
  readonly customClass = input<string>('');

  /**
   * Accessibility label override for the tag. If not provided, the label text is used.
   *
   * @default undefined
   */
  override readonly ariaLabel = input<string | undefined>(undefined);

  /** Emitted when the close button is clicked or activated via keyboard (Enter/Space). */
  readonly removed = output<void>();

  /** Computed signal for close button aria-label. */
  protected readonly closeButtonAriaLabel = computed(() => {
    const customLabel = this.ariaLabel();
    return customLabel || `Remove ${this.label()}`;
  });

  /** Computed signal for variant BEM modifier class. */
  protected readonly variantClass = computed(() => `hx-tag--${this.variant()}`);

  /** Computed signal for size BEM modifier class. */
  protected readonly sizeClass = computed(() => `hx-tag--${this.size()}`);

  /** Computed signal for color BEM modifier class. */
  protected readonly colorClass = computed(() => `hx-tag--${this.color()}`);

  /** Computed signal for disabled BEM modifier class. */
  protected readonly disabledClass = computed(() => (this.disabled() ? 'hx-tag--disabled' : ''));

  /** Computed signal for truncated BEM modifier class. */
  protected readonly truncatedClass = computed(() => (this.maxWidth() ? 'hx-tag--truncated' : ''));

  /** Computed signal that combines all BEM classes. */
  protected readonly hostClasses = computed(() => {
    const classes = [
      'hx-tag',
      this.variantClass(),
      this.sizeClass(),
      this.colorClass(),
      this.disabledClass(),
      this.truncatedClass(),
      this.customClass(),
    ];
    return classes.filter(Boolean).join(' ');
  });

  /** Computed signal for label truncation styles. */
  protected readonly truncationStyles = computed(() => {
    const maxWidth = this.maxWidth();
    return maxWidth ? { 'max-width': maxWidth, 'text-wrap': 'wrap' } : {};
  });

  /** Computed signal to check if start icon is present. */
  protected readonly hasStartIcon = computed(() => !!this.startIcon());

  ngAfterViewInit(): void {
    // Monitor focus on close button for focus-visible styling
    if (this.closeButtonElement) {
      this.focusMonitor.monitor(this.closeButtonElement.nativeElement, true);
    }
  }
  ngOnDestroy(): void {
    // Stop monitoring focus
    if (this.closeButtonElement) {
      this.focusMonitor.stopMonitoring(this.closeButtonElement.nativeElement);
    }
  }

  /** Handles click and keyboard events on the close button. Emits the removed event and announces to screen readers. */
  protected onRemoveClick(): void {
    if (this.disabled()) {
      return;
    }

    this.removed.emit();
    // Announce removal to screen readers
    void this.liveAnnouncer.announce(`${this.label()} removed`, 'polite');
  }

  /** Programmatically focus the close button. */
  focus(): void {
    this.closeButtonElement?.nativeElement?.focus();
  }

  /** Remove focus from the close button. */
  blur(): void {
    this.closeButtonElement?.nativeElement?.blur();
  }
}
